import React, { useState, useRef } from 'react';
import {
  BeakerIcon,
  ArrowUpTrayIcon,
  SparklesIcon,
  ClipboardDocumentIcon,
  ArrowPathIcon,
  QuestionMarkCircleIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  LinkIcon,
  HeartIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../services/api';
import { useTranslation } from 'react-i18next';
import { ExportDropdownMenu } from '../components/ui/ExportDropdownMenu';
import { LoadingOverlay } from '../components/ui/LoadingOverlay';

interface LabResult {
  test_name: string;
  full_name?: string;
  value: string;
  unit: string;
  reference_range: string;
  status: 'normal' | 'low' | 'high' | 'critical';
  interpretation: string;
  affected_organs?: string[];
  dietary_tips?: string;
}

interface DietaryResource {
  title: string;
  url: string;
  source: string;
  relevance: string;
}

interface OrganHealthResource {
  organ: string;
  title: string;
  url: string;
  source: string;
  relevance: string;
}

interface KeyFinding {
  finding: string;
  significance: string;
  action_needed: 'none' | 'discuss' | 'follow_up' | 'urgent';
}

interface DetectedPanel {
  id: string;
  name: string;
}

interface ResultSection {
  id: string;
  name: string;
  overview: string;
  results: LabResult[];
}

interface InterpretationResult {
  panel_type: string;
  panel_name: string;
  summary: string;
  results: LabResult[];
  sections?: ResultSection[];
  key_findings: KeyFinding[];
  patterns_detected: string[];
  lifestyle_considerations: string[];
  questions_for_doctor: string[];
  disclaimer: string;
  dietary_resources?: DietaryResource[];
  organ_health_resources?: OrganHealthResource[];
  detected_panels?: DetectedPanel[];
}

interface LabPanel {
  id: string;
  name: string;
  description: string;
  components: string[];
}

export const LabInterpreter: React.FC = () => {
  const { t, i18n } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [labText, setLabText] = useState('');
  const [selectedPanelType, setSelectedPanelType] = useState<string>('');
  const [simpleMode, setSimpleMode] = useState(false);
  const [wasFasting, setWasFasting] = useState<boolean | null>(null);
  const [isInterpreting, setIsInterpreting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [result, setResult] = useState<InterpretationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [selectedTermForExplanation, setSelectedTermForExplanation] = useState<string | null>(null);
  const [termExplanation, setTermExplanation] = useState<any>(null);
  const [isExplainingTerm, setIsExplainingTerm] = useState(false);

  const labPanels: LabPanel[] = [
    { id: '', name: t('labs:panels.auto', 'Auto-detect'), description: t('labs:panels.autoDesc', 'Let AI determine the panel type'), components: [] },
    { id: 'cbc', name: t('labs:panels.cbc', 'Complete Blood Count (CBC)'), description: t('labs:panels.cbcDesc', 'Blood cell analysis'), components: [] },
    { id: 'cmp', name: t('labs:panels.cmp', 'Comprehensive Metabolic Panel'), description: t('labs:panels.cmpDesc', 'Kidney, liver, electrolytes'), components: [] },
    { id: 'bmp', name: t('labs:panels.bmp', 'Basic Metabolic Panel'), description: t('labs:panels.bmpDesc', 'Kidney function, electrolytes'), components: [] },
    { id: 'lipid', name: t('labs:panels.lipid', 'Lipid Panel'), description: t('labs:panels.lipidDesc', 'Cholesterol and triglycerides'), components: [] },
    { id: 'thyroid', name: t('labs:panels.thyroid', 'Thyroid Panel'), description: t('labs:panels.thyroidDesc', 'Thyroid function tests'), components: [] },
    { id: 'a1c', name: t('labs:panels.a1c', 'Hemoglobin A1C'), description: t('labs:panels.a1cDesc', 'Diabetes monitoring'), components: [] },
    { id: 'iron', name: t('labs:panels.iron', 'Iron Panel'), description: t('labs:panels.ironDesc', 'Iron levels and capacity'), components: [] },
    { id: 'vitamin', name: t('labs:panels.vitamin', 'Vitamin Levels'), description: t('labs:panels.vitaminDesc', 'Vitamin D, B12, Folate'), components: [] },
    { id: 'liver', name: t('labs:panels.liver', 'Liver Function Panel'), description: t('labs:panels.liverDesc', 'Liver enzyme tests'), components: [] },
    { id: 'kidney', name: t('labs:panels.kidney', 'Kidney Function Panel'), description: t('labs:panels.kidneyDesc', 'Kidney health markers'), components: [] },
    { id: 'inflammatory', name: t('labs:panels.inflammatory', 'Inflammatory Markers'), description: t('labs:panels.inflammatoryDesc', 'CRP, ESR'), components: [] },
    { id: 'coagulation', name: t('labs:panels.coagulation', 'Coagulation Panel'), description: t('labs:panels.coagulationDesc', 'Blood clotting tests'), components: [] },
    { id: 'urinalysis', name: t('labs:panels.urinalysis', 'Urinalysis'), description: t('labs:panels.urinalysisDesc', 'Urine analysis'), components: [] },
  ];

  const handleInterpret = async () => {
    if (!labText.trim()) {
      setError(t('labs:errors.noText', 'Please paste or upload lab results to interpret.'));
      return;
    }

    setIsInterpreting(true);
    setError(null);
    setResult(null);

    try {
      const response = await apiClient.post('/labs/interpret', {
        lab_text: labText,
        panel_type: selectedPanelType || undefined,
        simple_explanation: simpleMode,
        fasting: wasFasting,
        language: i18n.language,
      });
      setResult(response.data);
      setExpandedSections({ summary: true, results: true, findings: true });
    } catch (err: any) {
      console.error('Interpretation failed:', err);
      setError(err.response?.data?.detail || t('labs:errors.interpretFailed', 'Failed to interpret the lab results. Please try again.'));
    } finally {
      setIsInterpreting(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['.pdf', '.docx', '.txt'];
    const fileExt = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));

    if (!allowedTypes.includes(fileExt)) {
      setError(t('labs:errors.unsupportedFile', 'Please upload a PDF, DOCX, or TXT file.'));
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      if (selectedPanelType) {
        formData.append('panel_type', selectedPanelType);
      }
      formData.append('simple_explanation', String(simpleMode));
      if (wasFasting !== null) {
        formData.append('fasting', String(wasFasting));
      }
      formData.append('language', i18n.language);

      const response = await apiClient.post('/labs/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setResult(response.data);
      setExpandedSections({ summary: true, results: true, findings: true });
      setLabText(`[Uploaded file: ${file.name}]`);
    } catch (err: any) {
      console.error('Upload failed:', err);
      setError(err.response?.data?.detail || t('labs:errors.uploadFailed', 'Failed to process the uploaded file. Please try pasting the text instead.'));
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleExplainTerm = async (term: string) => {
    setSelectedTermForExplanation(term);
    setIsExplainingTerm(true);

    try {
      const response = await apiClient.post('/labs/explain-term', null, {
        params: {
          term,
          simple_explanation: simpleMode,
        },
      });
      setTermExplanation(response.data);
    } catch (err) {
      console.error('Failed to explain term:', err);
      setTermExplanation({
        term,
        simple_definition: t('labs:errors.termExplainFailed', 'Unable to explain this term. Please ask your healthcare provider.'),
      });
    } finally {
      setIsExplainingTerm(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'normal':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'low':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'high':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
      case 'critical':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'none':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'discuss':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'follow_up':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'urgent':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleReset = () => {
    setLabText('');
    setResult(null);
    setError(null);
    setSelectedPanelType('');
    setWasFasting(null);
    setExpandedSections({});
    setTermExplanation(null);
    setSelectedTermForExplanation(null);
  };

  // Generate export content (markdown) — structure mirrors the on-screen UI
  const generateExportContent = (): string => {
    if (!result) return '';

    const lines: string[] = [];

    // Header / metadata
    if (result.detected_panels && result.detected_panels.length > 1) {
      lines.push(`**Detected Panels:** ${result.detected_panels.map((p) => p.name).join(', ')}`);
    } else {
      lines.push(`**Panel:** ${result.panel_name} (${result.panel_type})`);
    }
    lines.push(`**Date:** ${new Date().toLocaleDateString()}`);
    lines.push('');

    // Summary
    lines.push('## Summary');
    lines.push('');
    lines.push(result.summary);
    lines.push('');

    // Individual Results — grouped by section when available, else flat.
    // Status color codes (matching the on-screen Tailwind palette) are conveyed via
    // [[c:STATUS]]…[[/c]] markers around the test name, value, and status only —
    // not the interpretation, organs, or dietary tip text.
    const colorStatuses = new Set(['normal', 'low', 'high', 'critical']);
    const wrap = (status: string | undefined, body: string): string => {
      const s = (status || '').toLowerCase();
      return colorStatuses.has(s) ? `[[c:${s}]]${body}[[/c]]` : body;
    };

    const renderResultMarkdown = (r: LabResult) => {
      const heading =
        r.full_name && r.full_name !== r.test_name
          ? `${r.test_name} (${r.full_name})`
          : r.test_name;
      lines.push(`### ${wrap(r.status, heading)}`);
      lines.push('');
      if (r.status) {
        lines.push(wrap(r.status, `**Status:** ${r.status.toUpperCase()}`));
      }
      lines.push(wrap(r.status, `**Value:** ${r.value}${r.unit ? ` ${r.unit}` : ''}`));
      if (r.reference_range) {
        lines.push(`**Reference Range:** ${r.reference_range}`);
      }
      lines.push('');
      if (r.interpretation) {
        lines.push(r.interpretation);
      }
      if (r.affected_organs && r.affected_organs.length > 0) {
        lines.push('');
        lines.push(`**Affected Organs:** ${r.affected_organs.join(', ')}`);
      }
      if (r.dietary_tips) {
        lines.push('');
        lines.push(`**Dietary Tip:** ${r.dietary_tips}`);
      }
      lines.push('');
    };

    if (result.sections && result.sections.length > 0) {
      result.sections.forEach((section) => {
        lines.push(`## ${section.name} (${section.results.length})`);
        lines.push('');
        if (section.overview) {
          lines.push(`*${section.overview}*`);
          lines.push('');
        }
        section.results.forEach(renderResultMarkdown);
      });
    } else if (result.results.length > 0) {
      lines.push(`## Individual Results (${result.results.length})`);
      lines.push('');
      result.results.forEach(renderResultMarkdown);
    }

    // Key Findings — heading per finding, bold action label
    if (result.key_findings.length > 0) {
      lines.push('## Key Findings');
      lines.push('');
      result.key_findings.forEach((f) => {
        lines.push(`### ${f.finding}`);
        lines.push('');
        lines.push(`**Action Needed:** ${f.action_needed.replace(/_/g, ' ')}`);
        lines.push('');
        lines.push(f.significance);
        lines.push('');
      });
    }

    // Patterns Detected
    if (result.patterns_detected.length > 0) {
      lines.push('## Patterns Detected');
      lines.push('');
      result.patterns_detected.forEach((p) => lines.push(`- ${p}`));
      lines.push('');
    }

    // Lifestyle Considerations
    if (result.lifestyle_considerations.length > 0) {
      lines.push('## Lifestyle Considerations');
      lines.push('');
      result.lifestyle_considerations.forEach((l) => lines.push(`- ${l}`));
      lines.push('');
    }

    // Questions for Doctor (numbered list, mirrors UI)
    if (result.questions_for_doctor.length > 0) {
      lines.push('## Questions to Ask Your Doctor');
      lines.push('');
      result.questions_for_doctor.forEach((q, i) => lines.push(`${i + 1}. ${q}`));
      lines.push('');
    }

    // Dietary Resources — italic source, plain relevance, bold URL label
    if (result.dietary_resources && result.dietary_resources.length > 0) {
      lines.push('## Dietary & Lifestyle Resources');
      lines.push('');
      result.dietary_resources.forEach((r) => {
        lines.push(`### ${r.title}`);
        lines.push('');
        lines.push(`*${r.source}*`);
        lines.push('');
        lines.push(r.relevance);
        lines.push('');
        lines.push(`**URL:** ${r.url}`);
        lines.push('');
      });
    }

    // Organ Health Resources
    if (result.organ_health_resources && result.organ_health_resources.length > 0) {
      lines.push('## Organ Health Resources');
      lines.push('');
      result.organ_health_resources.forEach((r) => {
        lines.push(`### ${r.title}`);
        lines.push('');
        lines.push(`**Organ:** ${r.organ}`);
        lines.push(`*${r.source}*`);
        lines.push('');
        lines.push(r.relevance);
        lines.push('');
        lines.push(`**URL:** ${r.url}`);
        lines.push('');
      });
    }

    // Disclaimer
    lines.push('## Disclaimer');
    lines.push('');
    lines.push(result.disclaimer);
    lines.push('');
    lines.push('---');
    lines.push('');
    lines.push(`*Generated by Auxilio Health — ${new Date().toLocaleString()}*`);

    return lines.join('\n');
  };

  // Print lab results
  const printResults = () => {
    if (!result) return;

    const date = new Date().toLocaleDateString();
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const escapeHtml = (s: string) =>
      String(s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Lab Results - ${escapeHtml(result.panel_name)} - ${date}</title>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
              line-height: 1.6;
              color: #1f2937;
              max-width: 800px;
              margin: 0 auto;
              padding: 40px 20px;
            }
            h1 { font-size: 24px; margin-bottom: 5px; color: #1f2937; }
            h2 { font-size: 18px; color: #374151; margin-top: 20px; margin-bottom: 10px; border-bottom: 1px solid #e5e7eb; padding-bottom: 5px; }
            .subtitle { font-size: 14px; color: #6b7280; margin-bottom: 20px; }
            .panel-badge { display: inline-block; background: #d1fae5; color: #065f46; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 500; margin-right: 6px; }
            .summary { background: #f9fafb; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
            .result-item { margin-bottom: 15px; padding: 12px; border: 1px solid #e5e7eb; border-radius: 8px; page-break-inside: avoid; }
            .result-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
            .result-name { font-weight: 600; color: #1f2937; }
            .status { padding: 2px 8px; border-radius: 4px; font-size: 12px; font-weight: 500; }
            .status-normal { background: #d1fae5; color: #065f46; }
            .status-low { background: #dbeafe; color: #1e40af; }
            .status-high { background: #ffedd5; color: #c2410c; }
            .status-critical { background: #fee2e2; color: #b91c1c; }
            .result-value { font-size: 14px; color: #4b5563; }
            .result-interpretation { font-size: 13px; color: #6b7280; margin-top: 8px; }
            /* Status colors mirror the on-screen Tailwind palette (text-{green,blue,orange,red}-800). */
            .text-normal { color: #065f46; }
            .text-low { color: #1e40af; }
            .text-high { color: #c2410c; }
            .text-critical { color: #b91c1c; }
            .organ-pill { display: inline-block; background: #fee2e2; color: #b91c1c; padding: 2px 8px; border-radius: 12px; font-size: 11px; margin: 2px 4px 0 0; }
            .dietary-tip { margin-top: 8px; padding: 6px 8px; background: #ecfdf5; border-radius: 4px; font-size: 12px; color: #065f46; }
            .finding { margin-bottom: 10px; padding: 10px; background: #fffbeb; border-radius: 6px; page-break-inside: avoid; }
            .list-item { margin: 8px 0; padding-left: 20px; position: relative; }
            .list-item:before { content: "•"; position: absolute; left: 5px; color: #10b981; }
            .resource-item { margin-bottom: 12px; padding: 10px; background: #f0fdf4; border-radius: 6px; page-break-inside: avoid; }
            .resource-item.organ { background: #fef2f2; }
            .resource-title { font-weight: 600; color: #14532d; }
            .resource-item.organ .resource-title { color: #7f1d1d; }
            .resource-source { font-size: 11px; color: #6b7280; margin-bottom: 4px; }
            .resource-url { font-size: 11px; color: #2563eb; word-break: break-all; }
            .resource-relevance { font-size: 12px; color: #4b5563; margin-top: 4px; }
            .disclaimer { margin-top: 30px; padding: 15px; background: #fef3c7; border-radius: 8px; font-size: 12px; color: #92400e; }
            .footer { margin-top: 20px; text-align: center; font-size: 11px; color: #9ca3af; }
            @media print {
              body { padding: 20px; }
              .result-item, .finding, .resource-item { border: 1px solid #ccc; }
              a { color: inherit; text-decoration: none; }
            }
          </style>
        </head>
        <body>
          <h1>Lab Results Interpretation</h1>
          <p class="subtitle">Generated on ${date}</p>
          ${
            result.detected_panels && result.detected_panels.length > 1
              ? result.detected_panels.map((p) => `<span class="panel-badge">${escapeHtml(p.name)}</span>`).join('')
              : `<span class="panel-badge">${escapeHtml(result.panel_name)} (${escapeHtml(result.panel_type)})</span>`
          }

          <div class="summary">
            <h2>Summary</h2>
            <p>${escapeHtml(result.summary)}</p>
          </div>

          ${(() => {
            const colorableStatuses = new Set(['normal', 'low', 'high', 'critical']);
            const renderResult = (r: LabResult) => {
              const status = (r.status || '').toLowerCase();
              const colorClass = colorableStatuses.has(status) ? `text-${status}` : '';
              return `
              <div class="result-item">
                <div class="result-header">
                  <span class="result-name ${colorClass}">${escapeHtml(r.test_name)}${r.full_name ? ` (${escapeHtml(r.full_name)})` : ''}</span>
                  ${r.status ? `<span class="status status-${escapeHtml(r.status)}">${escapeHtml(r.status.toUpperCase())}</span>` : ''}
                </div>
                <div class="result-value">
                  <strong>Value:</strong> <span class="${colorClass}">${escapeHtml(r.value)} ${escapeHtml(r.unit || '')}</span>${r.reference_range ? ` | <strong>Reference:</strong> ${escapeHtml(r.reference_range)}` : ''}
                </div>
                ${r.interpretation ? `<div class="result-interpretation">${escapeHtml(r.interpretation)}</div>` : ''}
                ${r.affected_organs && r.affected_organs.length > 0 ? `
                  <div style="margin-top: 8px;">
                    <span style="font-size: 11px; color: #6b7280;">Organs:</span>
                    ${r.affected_organs.map(o => `<span class="organ-pill">${escapeHtml(o)}</span>`).join('')}
                  </div>
                ` : ''}
                ${r.dietary_tips ? `
                  <div class="dietary-tip"><strong>Dietary Tip:</strong> ${escapeHtml(r.dietary_tips)}</div>
                ` : ''}
              </div>
              `;
            };

            if (result.sections && result.sections.length > 0) {
              return result.sections.map(section => `
                <h2>${escapeHtml(section.name)} (${section.results.length})</h2>
                ${section.overview ? `<p style="font-style: italic; color: #4b5563; margin: 4px 0 12px;">${escapeHtml(section.overview)}</p>` : ''}
                ${section.results.map(renderResult).join('')}
              `).join('');
            }

            if (result.results.length > 0) {
              return `
                <h2>Individual Results (${result.results.length})</h2>
                ${result.results.map(renderResult).join('')}
              `;
            }

            return '';
          })()}

          ${result.key_findings.length > 0 ? `
            <h2>Key Findings</h2>
            ${result.key_findings.map(f => `
              <div class="finding">
                <strong>${escapeHtml(f.finding)}</strong>
                <p style="margin: 5px 0; font-size: 13px;">${escapeHtml(f.significance)}</p>
                <span class="status status-${f.action_needed === 'none' ? 'normal' : f.action_needed === 'urgent' ? 'critical' : 'high'}">
                  Action: ${escapeHtml(f.action_needed)}
                </span>
              </div>
            `).join('')}
          ` : ''}

          ${result.patterns_detected.length > 0 ? `
            <h2>Patterns Detected</h2>
            ${result.patterns_detected.map(p => `<div class="list-item">${escapeHtml(p)}</div>`).join('')}
          ` : ''}

          ${result.lifestyle_considerations.length > 0 ? `
            <h2>Lifestyle Considerations</h2>
            ${result.lifestyle_considerations.map(l => `<div class="list-item">${escapeHtml(l)}</div>`).join('')}
          ` : ''}

          ${result.questions_for_doctor.length > 0 ? `
            <h2>Questions for Your Doctor</h2>
            ${result.questions_for_doctor.map(q => `<div class="list-item">${escapeHtml(q)}</div>`).join('')}
          ` : ''}

          ${result.dietary_resources && result.dietary_resources.length > 0 ? `
            <h2>Dietary &amp; Lifestyle Resources</h2>
            ${result.dietary_resources.map(r => `
              <div class="resource-item">
                <div class="resource-title">${escapeHtml(r.title)}</div>
                <div class="resource-source">${escapeHtml(r.source)}</div>
                <div class="resource-url">${escapeHtml(r.url)}</div>
                <div class="resource-relevance">${escapeHtml(r.relevance)}</div>
              </div>
            `).join('')}
          ` : ''}

          ${result.organ_health_resources && result.organ_health_resources.length > 0 ? `
            <h2>Organ Health Resources</h2>
            ${result.organ_health_resources.map(r => `
              <div class="resource-item organ">
                <div style="font-size: 11px; font-weight: 600; color: #991b1b; margin-bottom: 2px;">${escapeHtml(r.organ)}</div>
                <div class="resource-title">${escapeHtml(r.title)}</div>
                <div class="resource-source">${escapeHtml(r.source)}</div>
                <div class="resource-url">${escapeHtml(r.url)}</div>
                <div class="resource-relevance">${escapeHtml(r.relevance)}</div>
              </div>
            `).join('')}
          ` : ''}

          <div class="disclaimer">
            <strong>Disclaimer:</strong> ${escapeHtml(result.disclaimer)}
          </div>

          <div class="footer">
            Generated by Auxilio Health • ${new Date().toLocaleString()}
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  // Handle export
  const handleExport = async (format: 'word' | 'pdf' | 'text') => {
    if (!result) return;

    const content = generateExportContent();
    const title = `Lab Results - ${result.panel_name}`;

    const formatMap: Record<string, string> = {
      'word': 'docx',
      'pdf': 'pdf',
      'text': 'txt',
    };

    try {
      const response = await apiClient.post('/export/document', {
        content,
        title,
        format: formatMap[format]
      }, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title}.${formatMap[format]}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Loading Overlay for file uploads */}
      <LoadingOverlay
        isVisible={isUploading}
        message={t('labs:loading.uploading', 'Processing...')}
        subMessage={t('labs:loading.pleaseWait', 'Analyzing your lab results')}
      />

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-2">
          <BeakerIcon className="h-8 w-8 text-emerald-600 dark:text-emerald-400" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('labs:title', 'Lab Results Interpreter')}
          </h1>
        </div>
        <p className="text-gray-600 dark:text-gray-400">
          {t('labs:subtitle', 'Upload or paste your lab results to get a plain-language explanation')}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="space-y-4">
          {/* Panel Type Selection */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('labs:panelType', 'Lab Panel Type')}
            </label>
            <select
              value={selectedPanelType}
              onChange={(e) => setSelectedPanelType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
              {labPanels.map((panel) => (
                <option key={panel.id} value={panel.id}>
                  {panel.name} - {panel.description}
                </option>
              ))}
            </select>
          </div>

          {/* Options Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Simple Mode Toggle */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={simpleMode}
                  onChange={(e) => setSimpleMode(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 dark:peer-focus:ring-emerald-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-emerald-600"></div>
                <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('labs:simpleMode', 'Simple mode')}
                </span>
              </label>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {t('labs:simpleModeDesc', 'Use everyday language')}
              </p>
            </div>

            {/* Fasting Status */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('labs:fastingStatus', 'Were you fasting?')}
              </label>
              <div className="flex space-x-2">
                <button
                  onClick={() => setWasFasting(true)}
                  className={`flex-1 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    wasFasting === true
                      ? 'bg-emerald-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {t('labs:yes', 'Yes')}
                </button>
                <button
                  onClick={() => setWasFasting(false)}
                  className={`flex-1 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    wasFasting === false
                      ? 'bg-emerald-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {t('labs:no', 'No')}
                </button>
                <button
                  onClick={() => setWasFasting(null)}
                  className={`flex-1 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    wasFasting === null
                      ? 'bg-emerald-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {t('labs:unsure', 'Unsure')}
                </button>
              </div>
            </div>
          </div>

          {/* Lab Results Input */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('labs:labText', 'Lab Results')}
            </label>
            <textarea
              value={labText}
              onChange={(e) => setLabText(e.target.value)}
              placeholder={t('labs:labPlaceholder', 'Paste your lab results here...')}
              className="w-full h-64 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
            />

            {/* File Upload */}
            <div className="mt-3 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx,.txt"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="flex items-center space-x-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg cursor-pointer transition-colors"
                >
                  {isUploading ? (
                    <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  ) : (
                    <ArrowUpTrayIcon className="h-4 w-4" />
                  )}
                  <span>{isUploading ? t('labs:uploading', 'Uploading...') : t('labs:uploadFile', 'Upload PDF/DOCX/TXT')}</span>
                </label>
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {t('labs:charCount', '{{count}} characters', { count: labText.length })}
              </span>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-400" />
                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-3">
            <button
              onClick={handleInterpret}
              disabled={isInterpreting || !labText.trim()}
              className="flex-1 flex items-center justify-center space-x-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white font-medium rounded-lg transition-colors"
            >
              {isInterpreting ? (
                <>
                  <ArrowPathIcon className="h-5 w-5 animate-spin" />
                  <span>{t('labs:interpreting', 'Interpreting...')}</span>
                </>
              ) : (
                <>
                  <SparklesIcon className="h-5 w-5" />
                  <span>{t('labs:interpret', 'Interpret Results')}</span>
                </>
              )}
            </button>
            {result && (
              <button
                onClick={handleReset}
                className="px-4 py-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg transition-colors"
              >
                {t('labs:reset', 'Reset')}
              </button>
            )}
          </div>
        </div>

        {/* Results Section */}
        <div className="space-y-4">
          {!result && !isInterpreting && (
            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700 p-8 text-center">
              <BeakerIcon className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <p className="text-gray-600 dark:text-gray-400">
                {t('labs:noResultYet', 'Paste or upload your lab results and click "Interpret Results" to see the analysis.')}
              </p>
            </div>
          )}

          {isInterpreting && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8 text-center">
              <ArrowPathIcon className="h-12 w-12 mx-auto mb-4 text-emerald-500 animate-spin" />
              <p className="text-gray-600 dark:text-gray-400">
                {t('labs:analyzingResults', 'Analyzing your lab results...')}
              </p>
            </div>
          )}

          {result && (
            <div className="space-y-4">
              {/* Panel Type Badge with Export/Print buttons */}
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {result.detected_panels && result.detected_panels.length > 1
                        ? t('labs:detectedPanels', 'Detected Panels')
                        : t('labs:detectedPanel', 'Detected Panel')}
                    </span>
                    {result.detected_panels && result.detected_panels.length > 1 ? (
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {result.detected_panels.map((panel) => (
                          <span
                            key={panel.id}
                            className="px-2.5 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-sm font-medium rounded-full"
                          >
                            {panel.name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {result.panel_name}
                      </h3>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {!(result.detected_panels && result.detected_panels.length > 1) && (
                      <span className="px-3 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-sm font-medium rounded-full">
                        {result.panel_type}
                      </span>
                    )}
                    <div className="border-l border-gray-300 dark:border-gray-600 h-6 mx-1"></div>
                    {/* Export/Print Dropdown */}
                    <ExportDropdownMenu
                      onExport={handleExport}
                      onPrint={printResults}
                      showPrint={true}
                    />
                  </div>
                </div>
              </div>

              {/* Summary */}
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                <button
                  onClick={() => toggleSection('summary')}
                  className="w-full flex items-center justify-between text-left"
                >
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
                    <InformationCircleIcon className="h-5 w-5 text-emerald-600" />
                    <span>{t('labs:summary', 'Summary')}</span>
                  </h3>
                  {expandedSections['summary'] ? (
                    <ChevronUpIcon className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                  )}
                </button>
                {expandedSections['summary'] && (
                  <p className="mt-3 text-gray-700 dark:text-gray-300">{result.summary}</p>
                )}
              </div>

              {/* Individual Results — grouped by section when available */}
              {(() => {
                // Map a result status to a Tailwind text color so the test name + value
                // visually match the status badge — same scheme used in the exports.
                const getStatusTextColor = (status: string): string => {
                  switch (status) {
                    case 'normal':   return 'text-green-700 dark:text-green-400';
                    case 'low':      return 'text-blue-700 dark:text-blue-400';
                    case 'high':     return 'text-orange-700 dark:text-orange-400';
                    case 'critical': return 'text-red-700 dark:text-red-400';
                    default:         return 'text-gray-900 dark:text-white';
                  }
                };
                const renderResultCard = (labResult: LabResult, index: number) => {
                  const textColor = getStatusTextColor(labResult.status);
                  return (
                  <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-lg p-3">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <button
                          onClick={() => handleExplainTerm(labResult.test_name)}
                          className={`font-medium ${textColor} hover:text-emerald-600 dark:hover:text-emerald-400`}
                        >
                          {labResult.test_name}
                        </button>
                        {labResult.full_name && labResult.full_name !== labResult.test_name && (
                          <p className="text-xs text-gray-500 dark:text-gray-400">{labResult.full_name}</p>
                        )}
                      </div>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded ${getStatusColor(labResult.status)}`}>
                        {t(`labs:status.${labResult.status}`, labResult.status)}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2 mb-2">
                      <span className={`text-lg font-semibold ${textColor}`}>
                        {labResult.value}
                      </span>
                      <span className={`text-sm ${textColor}`}>
                        {labResult.unit}
                      </span>
                      {labResult.reference_range && (
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          (Ref: {labResult.reference_range})
                        </span>
                      )}
                    </div>
                    {labResult.interpretation && (
                      <p className="text-sm text-gray-600 dark:text-gray-400">{labResult.interpretation}</p>
                    )}
                    {labResult.affected_organs && labResult.affected_organs.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        <span className="text-xs text-gray-500 dark:text-gray-400">{t('labs:affectedOrgans', 'Organs:')} </span>
                        {labResult.affected_organs.map((organ, i) => (
                          <span key={i} className="px-2 py-0.5 text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-full">
                            {organ}
                          </span>
                        ))}
                      </div>
                    )}
                    {labResult.dietary_tips && (
                      <div className="mt-2 p-2 bg-green-50 dark:bg-green-900/20 rounded text-xs text-green-700 dark:text-green-300">
                        <span className="font-medium">{t('labs:dietaryTip', 'Dietary Tip:')} </span>
                        {labResult.dietary_tips}
                      </div>
                    )}
                  </div>
                  );
                };

                // Section-grouped layout (mirrors the source report's panels).
                if (result.sections && result.sections.length > 0) {
                  return result.sections.map((section) => {
                    const sectionKey = `section:${section.id}`;
                    const expanded = expandedSections[sectionKey] !== false; // default open
                    return (
                      <div
                        key={section.id}
                        className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4"
                      >
                        <button
                          onClick={() => toggleSection(sectionKey)}
                          className="w-full flex items-center justify-between text-left"
                        >
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
                            <ClipboardDocumentIcon className="h-5 w-5 text-blue-600" />
                            <span>{section.name}</span>
                            <span className="text-sm font-normal text-gray-500">({section.results.length})</span>
                          </h3>
                          {expanded ? (
                            <ChevronUpIcon className="h-5 w-5 text-gray-500" />
                          ) : (
                            <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                          )}
                        </button>
                        {expanded && (
                          <div className="mt-3 space-y-3">
                            {section.overview && (
                              <p className="text-sm text-gray-600 dark:text-gray-400 italic">
                                {section.overview}
                              </p>
                            )}
                            {section.results.map((r, i) => renderResultCard(r, i))}
                          </div>
                        )}
                      </div>
                    );
                  });
                }

                // Fallback flat layout (used when sections aren't returned).
                if (result.results.length > 0) {
                  return (
                    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                      <button
                        onClick={() => toggleSection('results')}
                        className="w-full flex items-center justify-between text-left"
                      >
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
                          <ClipboardDocumentIcon className="h-5 w-5 text-blue-600" />
                          <span>{t('labs:individualResults', 'Individual Results')}</span>
                          <span className="text-sm font-normal text-gray-500">({result.results.length})</span>
                        </h3>
                        {expandedSections['results'] ? (
                          <ChevronUpIcon className="h-5 w-5 text-gray-500" />
                        ) : (
                          <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                        )}
                      </button>
                      {expandedSections['results'] && (
                        <div className="mt-3 space-y-3">
                          {result.results.map((r, i) => renderResultCard(r, i))}
                        </div>
                      )}
                    </div>
                  );
                }

                return null;
              })()}

              {/* Key Findings */}
              {result.key_findings.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                  <button
                    onClick={() => toggleSection('findings')}
                    className="w-full flex items-center justify-between text-left"
                  >
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
                      <ExclamationTriangleIcon className="h-5 w-5 text-amber-600" />
                      <span>{t('labs:keyFindings', 'Key Findings')}</span>
                    </h3>
                    {expandedSections['findings'] ? (
                      <ChevronUpIcon className="h-5 w-5 text-gray-500" />
                    ) : (
                      <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                    )}
                  </button>
                  {expandedSections['findings'] && (
                    <div className="mt-3 space-y-3">
                      {result.key_findings.map((finding, index) => (
                        <div key={index} className="border-l-4 border-amber-400 pl-3 py-2">
                          <div className="flex items-start justify-between">
                            <p className="font-medium text-gray-900 dark:text-white">{finding.finding}</p>
                            <span className={`ml-2 px-2 py-0.5 text-xs font-medium rounded ${getActionColor(finding.action_needed)}`}>
                              {t(`labs:action.${finding.action_needed}`, finding.action_needed)}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{finding.significance}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Patterns Detected */}
              {result.patterns_detected.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center space-x-2">
                    <SparklesIcon className="h-5 w-5 text-purple-600" />
                    <span>{t('labs:patternsDetected', 'Patterns Detected')}</span>
                  </h3>
                  <ul className="space-y-2">
                    {result.patterns_detected.map((pattern, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <span className="text-purple-500 mt-1">•</span>
                        <span className="text-gray-700 dark:text-gray-300">{pattern}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Lifestyle Considerations */}
              {result.lifestyle_considerations.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center space-x-2">
                    <CheckCircleIcon className="h-5 w-5 text-green-600" />
                    <span>{t('labs:lifestyleConsiderations', 'Lifestyle Considerations')}</span>
                  </h3>
                  <ul className="space-y-2">
                    {result.lifestyle_considerations.map((consideration, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <span className="text-green-500 mt-1">•</span>
                        <span className="text-gray-700 dark:text-gray-300">{consideration}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Questions for Doctor */}
              {result.questions_for_doctor.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center space-x-2">
                    <QuestionMarkCircleIcon className="h-5 w-5 text-indigo-600" />
                    <span>{t('labs:questionsForDoctor', 'Questions to Ask Your Doctor')}</span>
                  </h3>
                  <ul className="space-y-2">
                    {result.questions_for_doctor.map((question, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <span className="text-indigo-500 mt-1">{index + 1}.</span>
                        <span className="text-gray-700 dark:text-gray-300">{question}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Dietary Resources */}
              {result.dietary_resources && result.dietary_resources.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center space-x-2">
                    <LinkIcon className="h-5 w-5 text-green-600" />
                    <span>{t('labs:dietaryResources', 'Dietary & Lifestyle Resources')}</span>
                  </h3>
                  <div className="space-y-3">
                    {result.dietary_resources.map((resource, index) => (
                      <a
                        key={index}
                        href={resource.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block p-3 bg-green-50 dark:bg-green-900/20 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-medium text-green-800 dark:text-green-300">{resource.title}</h4>
                            <p className="text-xs text-green-600 dark:text-green-400 mb-1">{resource.source}</p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">{resource.relevance}</p>
                          </div>
                          <LinkIcon className="h-4 w-4 text-green-500 flex-shrink-0 ml-2 mt-1" />
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Organ Health Resources */}
              {result.organ_health_resources && result.organ_health_resources.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center space-x-2">
                    <HeartIcon className="h-5 w-5 text-red-600" />
                    <span>{t('labs:organHealthResources', 'Organ Health Resources')}</span>
                  </h3>
                  <div className="space-y-3">
                    {result.organ_health_resources.map((resource, index) => (
                      <a
                        key={index}
                        href={resource.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block p-3 bg-red-50 dark:bg-red-900/20 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="px-2 py-0.5 text-xs bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-200 rounded-full">
                                {resource.organ}
                              </span>
                            </div>
                            <h4 className="font-medium text-red-800 dark:text-red-300">{resource.title}</h4>
                            <p className="text-xs text-red-600 dark:text-red-400 mb-1">{resource.source}</p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">{resource.relevance}</p>
                          </div>
                          <LinkIcon className="h-4 w-4 text-red-500 flex-shrink-0 ml-2 mt-1" />
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Disclaimer */}
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800 dark:text-amber-200">{result.disclaimer}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Term Explanation Modal */}
      {selectedTermForExplanation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-lg w-full p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{selectedTermForExplanation}</h3>
              <button
                onClick={() => {
                  setSelectedTermForExplanation(null);
                  setTermExplanation(null);
                }}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            {isExplainingTerm ? (
              <div className="flex items-center justify-center py-8">
                <ArrowPathIcon className="h-8 w-8 text-emerald-500 animate-spin" />
              </div>
            ) : termExplanation ? (
              <div className="space-y-3">
                {termExplanation.full_name && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                      {t('labs:termModal.fullName', 'Full Name')}
                    </h4>
                    <p className="text-gray-700 dark:text-gray-300">{termExplanation.full_name}</p>
                  </div>
                )}
                <div>
                  <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                    {t('labs:termModal.definition', 'Definition')}
                  </h4>
                  <p className="text-gray-700 dark:text-gray-300">{termExplanation.simple_definition}</p>
                </div>
                {termExplanation.what_it_measures && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                      {t('labs:termModal.whatItMeasures', 'What It Measures')}
                    </h4>
                    <p className="text-gray-700 dark:text-gray-300">{termExplanation.what_it_measures}</p>
                  </div>
                )}
                {termExplanation.why_it_matters && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                      {t('labs:termModal.whyItMatters', 'Why It Matters')}
                    </h4>
                    <p className="text-gray-700 dark:text-gray-300">{termExplanation.why_it_matters}</p>
                  </div>
                )}
                {termExplanation.factors_affecting && termExplanation.factors_affecting.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                      {t('labs:termModal.factorsAffecting', 'Factors That Can Affect Results')}
                    </h4>
                    <ul className="text-gray-700 dark:text-gray-300 text-sm list-disc list-inside">
                      {termExplanation.factors_affecting.map((factor: string, index: number) => (
                        <li key={index}>{factor}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {termExplanation.related_tests && termExplanation.related_tests.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                      {t('labs:termModal.relatedTests', 'Related Tests')}
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {termExplanation.related_tests.map((test: string, index: number) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm rounded"
                        >
                          {test}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      )}

    </div>
  );
};

export default LabInterpreter;
